import { useCallback, useMemo, useState } from 'react';
import { NodeIndexOutlined, RightOutlined } from '@ant-design/icons';

import { DataTable, Tile } from '../../../../components/shared';
import { Badge, Button, Empty, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import type { BoDich } from '../../../../i18n';
import { KHOI_BANG, LE_BANG } from '../../chung/bang';
import { KhoiLoi } from '../../chung/components/KhoiLoi';
import { useDanhMuc, type BoDanhMuc } from '../../chung/hooks/useDanhMuc';
import { LOC_AI_MAC_DINH, thamSoLocTrace, type LocAi } from '../../chung/nghiep-vu/loc-ai';
import { soNguyen } from '../../chung/nghiep-vu/so-thong-ke';
import { TRONG, dayDu, gio } from '../../chung/nghiep-vu/thoi-gian';
import type { DongTrace, SapXep } from '../../chung/nghiep-vu/types-vet';
import { useDanhSachTrace } from '../hooks';
import { nhanTenant, useNguonVet } from '../nguon';
import { MA_LLM_GOI, gopTrace, khoangMs } from '../nghiep-vu/vet';
import { ChanDanhSach } from './ChanDanhSach';
import { ChonSapXep } from './ChonSapXep';
import { ChuoiBuoc } from './ChuoiBuoc';
import { ChiPhi, GiaVon, NhanGiaThieu, NhanKhongDangTin, TietKiemDem } from './NhanTinCay';

/**
 * Danh sách chuỗi xử lý — cửa vào của dòng chảy token. Port `vet/BangTrace.tsx` native.
 *
 * Mỗi dòng là một `trace_id`, tức MỘT việc người dùng đã làm, đi qua nhiều service. Bấm dòng (hoặc nút cuối dòng — `<tr>`
 * không nằm trong thứ tự tab) là mở waterfall các bước. Một hành động duy nhất trên dòng nên là nút, không phải menu `⋮`.
 */

const SO_SERVICE_HIEN = 3;
const SO_KY_TU_MA = 8;

/**
 * Tên một lượt xử lý. Lượt mở đầu bằng một lượt gọi mô hình thì KHÔNG dùng nhãn của danh mục: câu của `llm.goi` chỉ có
 * nghĩa khi đã thế số token.
 */
function tieuDeDong(r: DongTrace, t: BoDich['t'], danhMuc: BoDanhMuc, locale: string): string {
  if (!r.hanh_dong_dau) return TRONG;
  if (r.doi_tuong_loai === 'chatbot' && r.doi_tuong_ten) {
    return t('nktvSuDungAi.trace.hoiChatbot', { ten: r.doi_tuong_ten });
  }
  if (r.hanh_dong_dau === MA_LLM_GOI) {
    return t('nktvSuDungAi.trace.tieuDeCoAi', { so: soNguyen(r.so_luot_llm, locale) });
  }
  return danhMuc.nhan(r.hanh_dong_dau);
}

function Services({ cac }: { cac: string[] | null }) {
  if (!cac || cac.length === 0) return <span className="text-xs text-content-muted">{TRONG}</span>;
  const hien = cac.slice(0, SO_SERVICE_HIEN);
  const con = cac.length - hien.length;
  return (
    <span className="flex flex-wrap gap-1" title={cac.join(', ')}>
      {hien.map((s) => (
        <Badge key={s} variant="neutral">
          {s}
        </Badge>
      ))}
      {con > 0 && <Badge variant="neutral">{`+${con}`}</Badge>}
    </span>
  );
}

export function BangTrace({ tu, den, loc = LOC_AI_MAC_DINH }: { tu: string; den: string; loc?: LocAi }) {
  const { t, locale } = useT();
  const danhMuc = useDanhMuc();
  const { cotTenant, tenTenant } = useNguonVet();
  const [sapXep, setSapXep] = useState<SapXep>('moi_nhat');
  const [dangXem, setDangXem] = useState<DongTrace | null>(null);

  const locTrace = useMemo(() => thamSoLocTrace(loc), [loc]);
  const thamSo = useMemo(() => ({ tu, den, sap_xep: sapXep, ...locTrace }), [tu, den, sapXep, locTrace]);
  const coLoc = Object.keys(locTrace).length > 0;
  const truyVan = useDanhSachTrace(thamSo);
  const dong = useMemo(() => gopTrace(truyVan.data?.pages), [truyVan.data]);
  const coGiaVon = dong.some((r) => r.chi_phi_von !== undefined);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = truyVan;
  const taiThem = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="space-y-4">
      <p className="text-xs leading-relaxed text-content-muted">{t('nktvSuDungAi.trace.goiY')}</p>
      <Tile className={KHOI_BANG}>
        <div className="border-b border-default">
          <ChonSapXep giaTri={sapXep} onDoi={setSapXep} />
        </div>
        {truyVan.isPending ? (
          <Skeleton className="m-4 h-72" />
        ) : truyVan.isError ? (
          <KhoiLoi loi={truyVan.error} onThuLai={() => void truyVan.refetch()} />
        ) : (
          <>
            <DataTable<DongTrace>
              caption={t('nktvSuDungAi.trace.bang')}
              className={LE_BANG}
              // Cổng nền tảng gộp nhiều tenant: `trace_id` chỉ duy nhất trong một tenant.
              rowKey={(r) => `${r.tenant_id ?? ''}:${r.trace_id}`}
              size="small"
              dataSource={dong}
              pagination={false}
              onRow={(r) => ({ onClick: () => setDangXem(r), className: 'cursor-pointer' })}
              locale={{
                emptyText: coLoc ? (
                  <Empty icon={NodeIndexOutlined} title={t('nktvSuDungAi.danhSach.rongLoc')} />
                ) : (
                  <Empty
                    icon={NodeIndexOutlined}
                    title={t('nktvSuDungAi.trace.rongTieuDe')}
                    description={t('nktvSuDungAi.trace.rongMoTa')}
                  />
                ),
              }}
              columns={[
                {
                  title: t('nktvSuDungAi.trace.cotBatDau'),
                  width: 92,
                  render: (_v, r) => (
                    <time
                      dateTime={r.bat_dau}
                      title={`${dayDu(r.bat_dau, locale)} → ${dayDu(r.ket_thuc, locale)}`}
                      className="font-mono text-xs text-content-secondary"
                    >
                      {gio(r.bat_dau, locale)}
                    </time>
                  ),
                },
                {
                  title: t('nktvSuDungAi.trace.cotChuoi'),
                  width: 280,
                  render: (_v, r) => (
                    <div className="min-w-0 max-w-[17rem]">
                      <div className="truncate text-sm text-content-primary">{tieuDeDong(r, t, danhMuc, locale)}</div>
                      <div className="truncate text-xs text-content-muted">
                        {r.doi_tuong_loai !== 'chatbot' && (r.doi_tuong_ten ?? r.doi_tuong_id)
                          ? (r.doi_tuong_ten ?? r.doi_tuong_id)
                          : t('nktvSuDungAi.trace.maTraCuuNgan', { ma: r.trace_id.slice(0, SO_KY_TU_MA) })}
                      </div>
                    </div>
                  ),
                },
                ...(cotTenant
                  ? [
                      {
                        title: t('nktvSuDungAi.cotTenant'),
                        width: 160,
                        render: (_v: unknown, r: DongTrace) => (
                          <span className="block max-w-[10rem] truncate text-sm text-content-secondary">
                            {nhanTenant(tenTenant, r.tenant_id)}
                          </span>
                        ),
                      },
                    ]
                  : []),
                {
                  title: t('nktvSuDungAi.trace.cotNguoi'),
                  width: 160,
                  render: (_v, r) => (
                    <span className="block max-w-[10rem] truncate text-sm text-content-secondary">
                      {r.actor_ten ?? t('nktv.nguoi.khongXacDinh')}
                    </span>
                  ),
                },
                {
                  title: t('nktvSuDungAi.trace.cotService'),
                  width: 200,
                  render: (_v, r) => <Services cac={r.cac_service} />,
                },
                {
                  title: t('nktvSuDungAi.trace.cotBuoc'),
                  width: 150,
                  render: (_v, r) => (
                    <span className="flex flex-wrap items-center gap-1 text-xs">
                      <span className="tabular-nums text-content-secondary">
                        {t('nktvSuDungAi.trace.soBuoc', { so: soNguyen(r.so_buoc, locale) })}
                      </span>
                      {r.so_luot_llm > 0 && (
                        <Badge variant="info">
                          {t('nktvSuDungAi.trace.soLuotLlm', { so: soNguyen(r.so_luot_llm, locale) })}
                        </Badge>
                      )}
                      {r.so_loi > 0 && (
                        <Badge variant="danger">
                          {t('nktvSuDungAi.trace.soLoi', { so: soNguyen(r.so_loi, locale) })}
                        </Badge>
                      )}
                    </span>
                  ),
                },
                {
                  title: t('nktvSuDungAi.trace.cotMoHinh'),
                  width: 200,
                  ellipsis: true,
                  render: (_v, r) => (
                    <span className="text-xs text-content-secondary" title={r.cac_mo_hinh?.join(', ')}>
                      {r.cac_mo_hinh?.filter(Boolean).join(', ') || TRONG}
                    </span>
                  ),
                },
                {
                  title: t('nktvSuDungAi.trace.cotToken'),
                  width: 100,
                  align: 'right',
                  render: (_v, r) => (
                    <span className="font-mono text-xs tabular-nums text-content-primary">
                      {soNguyen(r.tong_token, locale)}
                    </span>
                  ),
                },
                {
                  title: t('nktvSuDungAi.trace.cotChiPhi'),
                  width: 230,
                  render: (_v, r) => (
                    <span className="flex flex-wrap items-center gap-1">
                      {/* `trangThai` để `null`: ở mức chuỗi, `co_chua_co_gia` nghĩa là MỘT PHẦN lượt gọi thiếu giá —
                          số tiền hiện ra vẫn là tiền thật của phần còn lại. Nhãn đứng cạnh nói số ấy còn thiếu phần. */}
                      <ChiPhi chiPhi={r.chi_phi_ban} donVi={r.don_vi_tien} trangThai={null} />
                      <NhanGiaThieu
                        coChuaCoGia={r.co_chua_co_gia}
                        coThieuTyGia={r.co_thieu_ty_gia}
                        coThieuDongGia={r.co_thieu_dong_gia}
                      />
                      {r.dang_tin === false && <NhanKhongDangTin />}
                    </span>
                  ),
                },
                ...(coGiaVon
                  ? [
                      {
                        title: t('nktvSuDungAi.cotGiaVon'),
                        width: 130,
                        render: (_v: unknown, r: DongTrace) => <GiaVon giaTri={r.chi_phi_von} donVi={r.don_vi_tien} />,
                      },
                    ]
                  : []),
                {
                  title: t('nktvSuDungAi.trace.cotTietKiem'),
                  width: 130,
                  render: (_v, r) => <TietKiemDem giaTri={r.tiet_kiem_dem} donVi={r.don_vi_tien} />,
                },
                {
                  title: t('nktvSuDungAi.trace.cotKeoDai'),
                  width: 100,
                  align: 'right',
                  render: (_v, r) => (
                    <span className="font-mono text-xs tabular-nums text-content-secondary">
                      {khoangMs(r.keo_dai_ms, locale)}
                    </span>
                  ),
                },
                {
                  title: <span className="sr-only">{t('nktvSuDungAi.trace.cotMo')}</span>,
                  width: 64,
                  fixed: 'right',
                  render: (_v, r) => (
                    <span className="flex items-center justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<RightOutlined />}
                        aria-label={t('nktvSuDungAi.trace.moChuoi')}
                        title={t('nktvSuDungAi.trace.moChuoi')}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDangXem(r);
                        }}
                      />
                    </span>
                  ),
                },
              ]}
            />

            <ChanDanhSach
              coTrangTiep={hasNextPage === true}
              dangTai={isFetchingNextPage}
              onTaiThem={taiThem}
              soDong={dong.length}
              ghiChu={sapXep === 'ton_nhat' ? t('nktvSuDungAi.danhSach.xepHangMotTrang') : undefined}
            />
          </>
        )}
      </Tile>

      {dangXem !== null && (
        <ChuoiBuoc traceId={dangXem.trace_id} tenantId={dangXem.tenant_id} onDong={() => setDangXem(null)} />
      )}
    </div>
  );
}
